import React from "react";
import styled from "styled-components";
import * as Router from "next/router";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import axios from "axios";
import * as sentry from "@sentry/nextjs";

import Link from "../components/Link";
import config from "../config.client";
import * as formatDateAndTime from "../utils/formatDateAndTime";
import * as T from "./Typography";
import * as I from "./Icons";
import ImageInitials from "./ImageInitials";
import * as UserCtx from "./UserContext";
import Report from "./Report";

dayjs.extend(utc);

export default function ActivityPostHead(props) {
  const innerRef = React.useRef();
  const router = Router.useRouter();
  const userInfo = React.useContext(UserCtx.Context);
  const [isOpen, setIsOpen] = React.useState(false);
  const [isReport, setIsReport] = React.useState(false);
  const [deleteText, setDeleteText] = React.useState("Delete");
  React.useEffect(attachOutsideClick, [isOpen]);
  const data = React.useMemo(() => JSON.parse(props?.object ?? "{}"), []);
  const maybeActor = props.actor?.data;
  const unixTime = dayjs.utc(props.time).unix();
  const formattedTime = formatDateAndTime.format(unixTime);
  const onOpen = () => setIsOpen(true);

  return (
    <S.Container>
      {isReport && <Report onClose={() => setIsReport(false)} {...props} />}
      <S.InnerContainer>
        <ImageInitials size={"mid"} {...maybeActor} />
        <S.InfoContainer>
          <S.InfoTopContainer>{getIntoTopContent()}</S.InfoTopContainer>
          <T.CS>
            {maybeActor.isAmbassador && (
              <strong>AllBright Ambassador{" | "}</strong>
            )}
            {formattedTime}
          </T.CS>
        </S.InfoContainer>
      </S.InnerContainer>
      {userInfo.id && (
        <S.OptsContainer>
          <S.OptsIconContainer onClick={onOpen}>
            <I.ThreeDots />
          </S.OptsIconContainer>
          {isOpen && (
            <S.OptsInnerContainer ref={innerRef}>
              {getOptions().map(getElmOpts)}
            </S.OptsInnerContainer>
          )}
        </S.OptsContainer>
      )}
    </S.Container>
  );

  function getIntoTopContent() {
    const maybeActorName = maybeActor?.firstName + " " + maybeActor?.lastName;
    const [typeFeed] = (props.origin ?? "").split(":");
    const currId = maybeActor?.id ?? props.actor?.id;
    const maybeDataName = data.name;
    const isGroup = typeFeed === "group";

    if (isGroup) {
      return (
        <React.Fragment>
          <Link href={`/member/${currId}`}>
            <S.NameBold>{maybeActorName}</S.NameBold>
          </Link>
          <S.IconContainer>
            <I.Triangle />
          </S.IconContainer>
          <S.NameBold>{maybeDataName}</S.NameBold>
        </React.Fragment>
      );
    }

    if (userInfo.id) {
      return (
        <>
          <Link href={`/member/${currId}`}>
            <S.Name isHover>{maybeActorName} </S.Name>
          </Link>
          {maybeActor.isAmbassador && <I.Ambassador size={21} />}
        </>
      );
    }

    return <T.H4 isHover={true}>{maybeActor?.firstName}</T.H4>;
  }

  function attachOutsideClick() {
    if (!isOpen) return;
    document.addEventListener("click", onMaybeOutside);
    return () => document.removeEventListener("click", onMaybeOutside);

    function onMaybeOutside(evt) {
      if (innerRef.current?.contains(evt.target)) return;
      setIsOpen(false);
    }
  }

  function getOptions() {
    const name = router.route === "/home" ? "base" : router.query.id;
    const url = `/post/${props.id}?name=${name}`;
    const onView = () => router.push(url);
    const isAdmin = config.ALLBRIGHT === userInfo.id;
    const isMine = maybeActor.id === userInfo.id;
    const onReport = () => setIsReport(true);

    const opts = [
      { icon: <I.Stats />, text: "View Post", onClick: onView },
      { icon: <I.Write />, text: "Report", onClick: onReport },
    ];

    return isAdmin || isMine
      ? opts.concat([
          { type: "separator" },
          { icon: <I.Bin />, text: deleteText, onClick: onDelete },
        ])
      : opts;
  }

  function getElmOpts(elm, idx) {
    if (elm.type === "separator") {
      return <S.Separator key={idx} />;
    }

    const subProps = {
      onClick: elm.onClick,
      key: elm.text,
    };

    return (
      <S.OptsItemContainer {...subProps}>
        {elm.icon && <S.OptsItemIcon>{elm.icon}</S.OptsItemIcon>}
        <S.OptsItemText>{elm.text}</S.OptsItemText>
      </S.OptsItemContainer>
    );
  }

  async function onDelete() {
    setDeleteText("Deleting...");
    const body = { id: props.id, name: props.pageId || "base" };
    const url = "/api/stream.delete-activity";
    try {
      await axios.post(url, body);
    } catch (err) {
      sentry.captureException(err);
    }
  }
}

const S = {};

S.Container = styled.div`
  display: flex;
  justify-content: space-between;
`;

S.InnerContainer = styled.div`
  display: flex;
  align-items: center;
`;

S.InfoContainer = styled.div`
  margin-left: 10px;
`;

S.InfoTopContainer = styled.div`
  display: flex;
`;

S.OptsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

S.OptsIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  border-radius: 15px;
  :hover {
    cursor: pointer;
    background-color: rgb(228, 230, 235);
  }
`;

S.OptsInnerContainer = styled.div`
  position: absolute;
  top: 40px;
  right: 5px;
  width: 200px;
  /* height: 200px; */
  box-shadow:
    rgba(0, 0, 0, 0.2) 0px 12px 28px 0px,
    rgba(0, 0, 0, 0.1) 0px 2px 4px 0px,
    rgba(255, 255, 255, 0.5) 0px 0px 0px 1px inset;
  background: white;
  border-radius: 10px;
  z-index: 10;
  padding: 10px;
`;

S.OptsItemContainer = styled.div`
  display: flex;
  padding: 5px;
  border-radius: 5px;
  cursor: pointer;
  :hover {
    background: rgba(235, 235, 235, 0.8);
  }
`;

S.OptsItemIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  margin-right: 5px;
`;

S.Separator = styled.div`
  border-bottom: 1px solid rgba(220, 220, 220, 0.8);
  margin-left: 1px;
  margin-right: 1px;
  margin-bottom: 5px;
`;

S.OptsItemText = styled(T.CM)`
  opacity: 1;
  flex: 1;
  display: flex;
  align-items: center;
  font-size: 1.5rem;
`;

S.IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 5px;
  padding-bottom: 1px;
`;

S.PreviewContainer = styled.div`
  display: none;
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 260px;
  /* height: 100px; */
  box-shadow:
    rgba(0, 0, 0, 0.2) 0px 12px 28px 0px,
    rgba(0, 0, 0, 0.1) 0px 2px 4px 0px,
    rgba(255, 255, 255, 0.5) 0px 0px 0px 1px inset;
  background: white;
  border-radius: 10px;
  z-index: 10;
  padding: 10px;
`;

S.RelativeContainer = styled.div`
  position: relative;
  :hover {
    & > ${S.PreviewContainer} {
      display: block;
    }
  }
`;

S.PreviewTopContainer = styled.div`
  display: flex;
  align-items: center;
`;

S.PreviewActionContainer = styled.div`
  display: flex;
  margin-top: 10px;
`;

S.PreviewImage = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background: rgb(243, 196, 150);
  margin-right: 10px;
`;

S.PreviewName = styled.div``;

S.NameBold = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: rgb(5, 5, 5);
  line-height: 2rem;
  :hover {
    cursor: pointer;
    text-decoration: underline;
  }
`;

S.Name = styled(T.H5)`
  float: left;
  margin-right: 5px;
`;

S.ConnectButton = styled.div`
  flex: 1;
  color: white;
  background-color: rgb(40, 83, 107);
  border-radius: 5px;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 5px 10px;
  display: flex;
  align-items: center;
  justify-content: space-around;
  :hover {
    cursor: pointer;
    background-color: rgb(80, 95, 120);
  }
`;

S.SquareContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 5px;
  width: 40px;
  height: 35px;
  border-radius: 5px;
  background-color: rgb(228, 230, 235);
  :hover {
    cursor: pointer;
    background-color: rgb(208, 200, 205);
  }
`;
